import React, { useCallback, useEffect, useState } from 'react';
import type { ChangeEvent, KeyboardEventHandler } from 'react';
import {
    Input,
    InputGroup,
    InputRightElement,
    Popover,
    PopoverBody,
    PopoverContent,
    PopoverTrigger,
    Portal,
    useDisclosure,
} from '@chakra-ui/react';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { useDebounceValue } from 'usehooks-ts';
import {
    shouldQueryBloomreach,
    useGetBloomreachSearch,
    useGetSuggestedBrands,
} from '../../../../bff-api/bloomreach-recommendation/useBloomreachSearch';
import { useGTM, useNavigateTo } from '../../../../hooks';
import { useIsMobile } from '../../../../hooks/use-is-mobile/useIsMobile';
import { useABTestVariable } from '../../../../hooks/useABTestVariable';
import { useBuildURL } from '../../../../hooks/useBuildURL';
import { useSearchUrl } from '../../../../hooks/useSearchUrl';
import { CustomPreference } from '../../../../utils/api/CustomPreference';
import { isClient, isHydrated } from '../../../../utils/utils';
import { CircleCloseIcon, SearchIcon } from '../../../icons';
import { MenuSearchResults } from './menu-search-results';

interface Props {
    placeholder?: string;
    forceReloadOnSearch?: boolean;
    headerRef: React.RefObject<HTMLElement>;
}

export const MenuSearch: React.FC<Props> = ({ placeholder, forceReloadOnSearch, headerRef }) => {
    const { onSearch } = useGTM();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const intl = useIntl();
    const [searchTerm, setSearchTerm] = useState('');
    const location = useLocation();
    const pwaSearchMode = useABTestVariable<string>(CustomPreference.PWA_SEARCH_MODE, 'SFCC');
    const [debouncedSearchTerm] = useDebounceValue(searchTerm, 300);
    const [bloomreachParams, setBloomreachParams] = useState<{ url: string; refUrl: string }>({
        url: '',
        refUrl: '',
    });
    const { data, isLoading } = useGetBloomreachSearch({
        ...bloomreachParams,
        q: debouncedSearchTerm,
        pageSize: 4,
    });
    const brands = useGetSuggestedBrands({
        searchTerm: debouncedSearchTerm,
    });
    const navigateTo = useNavigateTo();
    const searchUrl: string = useSearchUrl();
    const { buildSearchProductURL } = useBuildURL();
    const isMobile = useIsMobile();

    const onClickResult = (): void => {
        onClose();
        setSearchTerm('');
    };

    const handleOnSearch = (e: ChangeEvent<HTMLInputElement>): void => {
        setSearchTerm(e.target.value);
        if (!e.target.value) {
            onClickResult();
        }
    };

    const onSubmitSearch = (): void => {
        if (!searchTerm) {
            return;
        }

        onSearch(searchTerm);

        onClickResult();

        if (
            location.pathname.startsWith(searchUrl) &&
            pwaSearchMode !== 'SFCC' &&
            !forceReloadOnSearch
        ) {
            dispatchEvent(new CustomEvent('slt-search', { detail: searchTerm }));
        } else {
            navigateTo(buildSearchProductURL(searchTerm), {
                useWindowLocation: pwaSearchMode === 'SFCC' || forceReloadOnSearch,
            });
        }
    };

    const handleClickOutside = useCallback(
        (event: MouseEvent) => {
            const target = event.target as Element;
            if (isOpen && !target.closest('[data-testid="header.menu.search.results"]')) {
                onClose();
            }
        },
        [isOpen, onClose],
    );

    const onClearSearch = (): void => {
        setSearchTerm('');
        onClose();
    };

    const onKeyDown: KeyboardEventHandler<HTMLInputElement> = (e): void => {
        if (e.key === 'Escape' || e.key === 'Esc') {
            e.preventDefault();
            onClickResult();
        } else if (e.key === 'Enter') {
            e.preventDefault();

            onSubmitSearch();
        } else if (shouldQueryBloomreach(searchTerm) && !isLoading) {
            onOpen();
        }
    };

    useEffect(() => {
        const handleScroll = (): void => {
            if (!isMobile) {
                onClose();
            }
        };

        window.addEventListener('scroll', handleScroll);

        return () => window.removeEventListener('scroll', handleScroll);
    }, [isMobile, onClose]);

    useEffect(() => {
        if (!isLoading && shouldQueryBloomreach(debouncedSearchTerm)) {
            onOpen();
        }
    }, [isLoading]);

    useEffect(() => {
        if (debouncedSearchTerm) {
            onSearch(debouncedSearchTerm);
        }
    }, [debouncedSearchTerm]);

    useEffect(() => {
        if (isHydrated() && window.document.URL) {
            setBloomreachParams({
                url: window.document.URL,
                refUrl: window.document.referrer || '',
            });
        }
    }, []);

    useEffect(() => {
        document.removeEventListener('click', handleClickOutside);

        document.addEventListener('click', handleClickOutside);

        return () => document.removeEventListener('click', handleClickOutside);
    }, [handleClickOutside]);

    const popoverHeight = getMobilePopoverHeight(headerRef, !!isMobile);

    return (
        <Popover
            variant="slt-outline"
            isOpen={isOpen}
            onClose={onClose}
            closeDelay={0}
            autoFocus={false}
        >
            <PopoverTrigger>
                <div className="w-full" data-testid="menu-search-container">
                    <InputGroup>
                        <Input
                            variant="slt-search"
                            data-testid="header.menu.search.input"
                            type="search"
                            autoComplete="off"
                            placeholder={
                                placeholder ??
                                intl.formatMessage({
                                    id: 'header.menu.search.placeholder',
                                    defaultMessage: 'What are you looking for?',
                                })
                            }
                            onKeyDown={onKeyDown}
                            onChange={handleOnSearch}
                            value={searchTerm}
                        />

                        <InputRightElement height="100%" width={6} marginX={4}>
                            {searchTerm ? (
                                <button
                                    type="button"
                                    className="inline-flex items-center justify-center"
                                    data-testid="header.menu.search.clear.button"
                                    onClick={onClearSearch}
                                >
                                    <CircleCloseIcon data-testid="header.menu.clear.search.icon" />
                                </button>
                            ) : (
                                <SearchIcon
                                    data-testid="header.menu.search.icon"
                                    width={4}
                                    height={4}
                                />
                            )}
                        </InputRightElement>
                    </InputGroup>
                </div>
            </PopoverTrigger>

            <Portal>
                <PopoverContent padding={{ md: 4 }}>
                    <PopoverBody maxHeight={popoverHeight} height={popoverHeight}>
                        <MenuSearchResults
                            searchTerm={debouncedSearchTerm}
                            phrases={data?.phrases ?? []}
                            categories={data?.categories ?? []}
                            brands={brands?.data?.brands ?? []}
                            hardgood={data?.hardgood ?? []}
                            culinary={data?.culinary ?? []}
                            recipe={data?.recipe ?? []}
                            onClickResult={onClickResult}
                            onClosePopover={onClose}
                        />
                    </PopoverBody>
                </PopoverContent>
            </Portal>
        </Popover>
    );
};

MenuSearch.defaultProps = {
    placeholder: undefined,
    forceReloadOnSearch: false,
};

const getMobilePopoverHeight = (
    headerRef: React.RefObject<HTMLElement>,
    isMobile: boolean,
): number | undefined => {
    const headerHeight = headerRef?.current?.getBoundingClientRect().height;

    if (isMobile && isClient && headerHeight) {
        return window.innerHeight - headerHeight;
    }

    return undefined;
};
